import { config } from '@config';
import { RxUtils, TypeUtils } from '@utils';
import { merge, Observable, of, throwError } from 'rxjs';
import { concatMap, tap, map, catchError, toArray } from 'rxjs/operators';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { getStore, RootState } from '@store';
import { setLoggedinUser } from '@store/slices/common.slice';
import { IUser } from './user.interface';
import { User } from './user.class';
import { CreateUser } from './create-user.class';
import { SelectableRole } from './selectable-role';

export class UserService {
	private store = getStore().store;
	private getUrl = () => `${config.apiUrl}/user`;

	create(user: CreateUser) {
		return httpService.post<User>(this.getUrl(), '', { body: user, errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.create.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(User, result))
		);
	}

	getAll(includeMe?: boolean) {
		const myEmail = (this.store.getState() as RootState).Common.loggedinUser?.email;
		return httpService.get<User[]>(this.getUrl()).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getAll.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(User, result)),
			map((users) => (!includeMe ? users.filter((user) => user.email !== myEmail) : users))
		);
	}

	getMe() {
		return httpService.get<IUser>(this.getUrl(), 'me').pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getMe.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => this.store.dispatch(setLoggedinUser(result)).payload),
			map((result) => TypeUtils.convert(User, result))
		);
	}

	getContactPersons() {
		return httpService.get<string[]>(this.getUrl(), 'contact-persons').pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getContactPersons.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}

	getSelectableRoles() {
		return httpService.get<SelectableRole[]>(this.getUrl(), 'selectable-roles').pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getSelectableRoles.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(SelectableRole, result))
		);
	}

	acceptTerms() {
		return httpService
			.get<unknown>(this.getUrl(), 'accept-terms')
			.pipe(tap((result) => logger.debug(`[${this.constructor.name}.${this.acceptTerms.name}]`, result)));
	}

	getByEmail(email: string) {
		return httpService.get<IUser>(this.getUrl(), email).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getByEmail.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(User, result))
		);
	}

	update({ email, ...user }: Partial<User>) {
		return httpService.patch<unknown>(this.getUrl(), `${email}`, { body: user, errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.update.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}

	delete(email: string) {
		return httpService.delete<unknown>(this.getUrl(), email, { errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.delete.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}

	deleteMultiple(emails: string[]) {
		return merge(...emails.map((e) => this.delete(e))).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.deleteMultiple.name}]`, result)),
			catchError((e) => {
				logger.error(e);
				return of(null);
			}),
			toArray()
		);
	}

	doIfNotAdmin<T = any>(callback: Observable<T>): Observable<T | undefined> {
		const loggedinUser = (this.store.getState() as RootState).Common.loggedinUser;
		if (!loggedinUser) {
			return throwError(() => 'No loggedin user');
		} else {
			const user = TypeUtils.convert(User, loggedinUser);
			if (user.isAdmin()) {
				return of(undefined);
			} else {
				return callback;
			}
		}
	}
}

export const userService = new UserService();
